import React, { useState } from 'react';

import styled from 'styled-components';

import { useDeleteFollowup, useDeleteFollowupGroup, useDeleteFollowupPeriodGroup } from '@hooks/API/Event/UseFollowup';

import { useFollowUp, ACTIONS } from '@components/EventDrawer/FollowUpEvent/FollowUpContext';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import NwIcon from '@components/Gui/NwIcon';
import SendProjectDrawerFromEvent from '@components/SendProjectAgendaDrawer/SendProjectDrawerFromEvent';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Wrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem 0.75rem 0.5rem;
    border-top: ${(props) => props.theme.DefaultBorder};
    border-bottom: ${(props) => props.theme.DefaultBorder};

    .left-content {
        text-align: left;
        width: 10rem;
        font-size: 1.75rem;

        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        small {
            font-size: ${(props) => props.theme.FontSizeXSm};
        }
    }
    .middle-content {
        flex: 1;
        text-align: left;
        padding-left: 0.5rem;
        h1 {
            margin: 0;
            font-size: ${(props) => props.theme.FontSizeLg};
            text-transform: capitalize;
        }
    }
    .right-content {
        text-align: center;
        width: 8rem;
    }
    &.is-cancelled {
        h1 {
            ${(props) => props.theme.RedLineThrough(1)}
        }
    }
`;

export const FollowUpTitle = ({ handleSelectProject }) => {
    const { groupType, followUpData, afterRemove, followUpID, dispatch, followUpStyle, followUpSubType } =
        useFollowUp();

    const [sendProjectDrawer, setSendProjectDrawer] = useState(false);

    const { mutateAsync: onDeleteFollowup } = useDeleteFollowup();
    const { mutateAsync: onDeleteFollowupGroup } = useDeleteFollowupGroup();
    const { mutateAsync: onDeleteFollowupPeriodGroup } = useDeleteFollowupPeriodGroup();

    const handleRemoveEvent = async () => {
        try {
            await onDeleteFollowup({ ID: followUpID, reloadID: null });
            afterRemove();
        } catch (error) {
            console.error('Remove Event Date Model Error => ', error);
        }
    };

    const handleRemoveEventGroup = async (groupGuid) => {
        try {
            await onDeleteFollowupGroup({ GroupGUID: groupGuid });
        } catch (error) {
            console.error('Remove Event Group Error => ', error);
        }
    };

    const handleRemoveEventPeriodGroup = async (periodGroupGuid) => {
        try {
            await onDeleteFollowupPeriodGroup({ PeriodGroupGUID: periodGroupGuid });
        } catch (error) {
            console.error('Remove Event Period Group Error => ', error);
        }
    };

    const handleRemoveGroup = async () => {
        if (groupType === 'period') {
            await handleRemoveEventPeriodGroup(followUpData.PeriodGroupGuid);
        } else {
            await handleRemoveEventGroup(followUpData.GroupGuid);
        }
        afterRemove(true);
    };

    const handleEditGeneral = () => {
        dispatch({ type: ACTIONS.TOGGLE_EDIT });
    };

    const handleProjectUnlink = () => handleSelectProject();

    const buttonactions = [
        {
            name: '',
            key: 'edit',
            pinned: true,
            icon: byPrefixAndName.fasl['pen'],
            action: handleEditGeneral,
        },
    ];

    if (followUpData.Project) {
        buttonactions.unshift({
            name: '',
            key: 'send-schedule',
            pinned: true,
            tooltip: 'Send schedule by e-mail',
            icon: byPrefixAndName.fasl['share'],
            action: () => setSendProjectDrawer(true),
        });
    }

    if (!groupType) {
        buttonactions.push({
            name: 'delete',
            type: 'delete',
            key: 'delete',
            icon: byPrefixAndName.fasl['trash-alt'],
            confirmationPlacement: 'left',
            action: handleRemoveEvent,
        });
    } else if (groupType) {
        buttonactions.push({
            name: 'delete all',
            type: 'delete',
            key: 'delete',
            icon: byPrefixAndName.fasl['trash-alt'],
            confirmationPlacement: 'left',
            action: handleRemoveGroup,
        });
    }

    if (followUpData.Project) {
        buttonactions.push({
            name: 'unlink from project',
            key: 'unlink-project',
            icon: byPrefixAndName.fasl['trash-alt'],
            action: handleProjectUnlink,
        });
    }

    const isGloballyCancelled = () => {
        if (followUpData.Cancel) {
            if (followUpData.RelatedEvents && followUpData.RelatedEvents.length > 0) {
                if (followUpData.RelatedEvents.every((e) => e.Cancel)) {
                    return true;
                }
                return false;
            }
            return true;
        }
        return false;
    };

    return (
        <Wrapper $isCancelled={isGloballyCancelled()}>
            <div className="left-content">
                <NwIcon icon={followUpStyle.icon} />
                <small>{followUpSubType ? followUpSubType.Label : 'Follow-up Activity'}</small>
            </div>
            <div className="middle-content">
                <h1>{followUpData.Title}</h1>
            </div>
            <div className="right-content">
                <NwDropDownMenu right actions={buttonactions} />
            </div>
            {sendProjectDrawer && (
                <SendProjectDrawerFromEvent
                    event={followUpData}
                    eventType="FollowUp"
                    onClose={() => setSendProjectDrawer(false)}
                />
            )}
        </Wrapper>
    );
};
